import type { BadgeColor } from 'app/components/ui/badge'

/** A patient on an AI thread waits this long for a reply before the report lists them (plan D33, D37). */
export const REPLY_WAIT_MINUTES = 5
export const REPLY_WAIT_LABEL = `5 min`

/** Waiting: no reply yet, right now. Late: the AI replied in the date range, but after the threshold. */
export type WaitStatus = `waiting` | `late`

export const WAIT_STATUSES: WaitStatus[] = [`waiting`, `late`]

export const WAIT_STATUS_LABEL: Record<WaitStatus, string> = {
  waiting: `Waiting`,
  late: `Replied Late`,
}

export const WAIT_STATUS_COLOR: Record<WaitStatus, BadgeColor> = {
  waiting: `red`,
  late: `amber`,
}

/** One patient who waited more than the threshold for a reply: who, where, since when, how long. */
export type ReplyWait = {
  status: WaitStatus
  threadId: string
  orgId: string
  workspaceId: string
  locationId: string
  /** The contact on the patient's text; the store resolves its name. */
  contactId?: string
  /** The contact's name, or the thread's name when the contact has none. */
  contactName?: string
  /** The number the patient texted from. */
  phone?: string
  /** When the patient's text that waited arrived. */
  textedDate: string
  /** Until the AI replied (late), or until the page loaded (waiting). */
  waitedMinutes: number
  /** The AI's last text on the thread, if any. */
  aiLastSpoke?: string
}

type ThreadState = {
  id: string
  orgId: string
  workspaceId: string
  locationId: string
  name?: string | null
  phone?: string | null
  lastIncomingMessageDate?: string | null
  lastOutgoingMessageDate?: string | null
  aiEnabled?: boolean | null
  agentLastMessageDate?: string | null
  agentReplyDueDate?: string | null
}
type ThreadMessage = {
  threadId?: string | null
  contactId?: string | null
  from?: string | null
  direction?: string | null
  createdDate: string
}

const THRESHOLD_MS = REPLY_WAIT_MINUTES * 60_000

function replyWait(status: WaitStatus, thread: ThreadState, text: ThreadMessage, waitedMs: number): ReplyWait {
  return {
    status,
    threadId: thread.id,
    orgId: thread.orgId,
    workspaceId: thread.workspaceId,
    locationId: thread.locationId,
    contactId: text.contactId ?? undefined,
    contactName: thread.name ?? undefined,
    phone: text.from ?? undefined,
    textedDate: text.createdDate,
    waitedMinutes: waitedMs / 60_000,
    aiLastSpoke: thread.agentLastMessageDate ?? undefined,
  }
}

/** Patients waiting on the AI now, longest first: the AI is on and the patient texted after the last send, more than the threshold ago. */
export function waitingOnAi(
  threads: ThreadState[],
  checkedAt: Date,
  contactIds: Record<string, string> = {},
): ReplyWait[] {
  return threads
    .flatMap((thread): ReplyWait[] => {
      const texted = thread.lastIncomingMessageDate
      if (!thread.aiEnabled || !texted) return []
      if (thread.lastOutgoingMessageDate && Date.parse(thread.lastOutgoingMessageDate) >= Date.parse(texted)) return []
      if (thread.agentReplyDueDate && Date.parse(thread.agentReplyDueDate) > checkedAt.getTime()) return []
      const waited = checkedAt.getTime() - Date.parse(texted)
      const text = { threadId: thread.id, contactId: contactIds[thread.id], from: thread.phone, createdDate: texted }
      return waited > THRESHOLD_MS ? [replyWait(`waiting`, thread, text, waited)] : []
    })
    .sort((a, b) => b.waitedMinutes - a.waitedMinutes)
}

/**
 * Patients the AI answered late in the date range, newest first (plan D37): a patient text
 * whose first AI reply came after the threshold. Only texts after the AI had spoken on the
 * thread count, so a text from before a workflow switched the AI on is not late. One row per
 * conversation, for its newest late text.
 */
export function lateReplies(
  patientTexts: ThreadMessage[],
  agentTexts: ThreadMessage[],
  threads: ThreadState[],
): ReplyWait[] {
  const threadsById = new Map(threads.map((thread) => [thread.id, thread]))
  const agentTimesByThread = new Map<string, number[]>()
  for (const text of agentTexts) {
    if (!text.threadId) continue
    const times = agentTimesByThread.get(text.threadId) ?? []
    times.push(Date.parse(text.createdDate))
    agentTimesByThread.set(text.threadId, times)
  }
  for (const times of agentTimesByThread.values()) times.sort((a, b) => a - b)

  const newestByThread = new Map<string, ReplyWait>()
  for (const text of patientTexts) {
    const thread = text.threadId ? threadsById.get(text.threadId) : undefined
    if (!thread) continue
    const received = Date.parse(text.createdDate)
    const agentTimes = agentTimesByThread.get(thread.id) ?? []
    if (!agentTimes.some((time) => time < received)) continue
    const reply = agentTimes.find((time) => time > received)
    if (reply === undefined || reply - received <= THRESHOLD_MS) continue
    const current = newestByThread.get(thread.id)
    if (!current || received > Date.parse(current.textedDate)) {
      newestByThread.set(thread.id, replyWait(`late`, thread, text, reply - received))
    }
  }
  return [...newestByThread.values()].sort((a, b) => Date.parse(b.textedDate) - Date.parse(a.textedDate))
}
